import React from 'react';
import { Profile, Qualification, WorkExperience, Verification, PassportStatusLevel, Document } from '../types';
import { isVerified, recordStatus } from '../utils/verification';

interface PassportStatusProps {
  profile: Profile | null;
  qualifications: Qualification[];
  verifications: Verification[];
  workExperience?: WorkExperience[];
  documents?: Document[];
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// eslint-disable-next-line react-refresh/only-export-components
export function calculatePassportStatus(
  profile: Profile | null,
  qualifications: Qualification[],
  workExperience: WorkExperience[] = [],
  documents: Document[] = []
): { level: PassportStatusLevel; label: string; description: string } {
  if (!profile || (!profile.first_name && !profile.last_name)) {
    return {
      level: 'red',
      label: 'Incomplete',
      description: 'Profile not set up',
    };
  }

  const profileComplete =
    !!profile.first_name &&
    !!profile.last_name &&
    !!profile.phone &&
    !!profile.contact_email &&
    !!profile.field_of_work &&
    !!profile.bio;

  const officialDocs = documents.filter(
    d => d.document_type === 'id_document' || d.document_type === 'police_clearance',
  );
  const awaitingVerification =
    qualifications.some(q => !isVerified(recordStatus(q))) ||
    workExperience.some(job => !isVerified(recordStatus(job))) ||
    officialDocs.some(d => !isVerified(recordStatus(d)));

  const contentComplete =
    profileComplete &&
    qualifications.length >= 1 &&
    workExperience.length >= 1;

  if (contentComplete) {
    if (awaitingVerification) {
      return {
        level: 'blue',
        label: 'Awaiting verification',
        description: 'Required items still need a verifier',
      };
    }
    return {
      level: 'green',
      label: 'Complete',
      description: 'Profile complete and required items verified',
    };
  }

  if (qualifications.length > 0 || workExperience.length > 0 || documents.length > 0) {
    if (awaitingVerification) {
      return {
        level: 'blue',
        label: 'Awaiting verification',
        description: 'Required items still need a verifier',
      };
    }
    return {
      level: 'amber',
      label: 'In Progress',
      description: 'Building credentials',
    };
  }

  return {
    level: 'red',
    label: 'Getting Started',
    description: 'Add qualifications, work experience and official documents',
  };
}

export const PassportStatusBadge: React.FC<PassportStatusProps> = ({
  profile,
  qualifications,
  workExperience = [],
  documents = [],
  showLabel = true,
  size = 'md',
}) => {
  const status = calculatePassportStatus(profile, qualifications, workExperience, documents);

  const colors = {
    red: 'bg-red-100 text-red-800 border-red-200',
    amber: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    blue: 'bg-sky-100 text-sky-800 border-sky-200',
    green: 'bg-green-100 text-green-800 border-green-200',
  };

  const dotColors = {
    red: 'bg-red-500',
    amber: 'bg-yellow-500',
    blue: 'bg-sky-500',
    green: 'bg-green-500',
  };

  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5',
  };

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-medium max-w-full ${colors[status.level]} ${sizes[size]}`}>
      <span className={`w-2 h-2 rounded-full ${dotColors[status.level]}`} />
      {status.label}
      {showLabel && size === 'lg' && (
        <span className="font-normal opacity-75 ml-1">— {status.description}</span>
      )}
    </span>
  );
};

export const PassportStatusCard: React.FC<PassportStatusProps> = ({
  profile,
  qualifications,
  workExperience = [],
  documents = [],
}) => {
  const status = calculatePassportStatus(profile, qualifications, workExperience, documents);

  const bgColors = {
    red: 'bg-red-50 border-red-200',
    amber: 'bg-yellow-50 border-yellow-200',
    blue: 'bg-sky-50 border-sky-200',
    green: 'bg-green-50 border-green-200',
  };

  const textColors = {
    red: 'text-red-700',
    amber: 'text-yellow-700',
    blue: 'text-sky-700',
    green: 'text-green-700',
  };

  const iconPaths = {
    red: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    amber: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    blue: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    green: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  };

  const chipBg =
    status.level === 'green'
      ? 'bg-emerald-100'
      : status.level === 'blue'
        ? 'bg-sky-100'
        : status.level === 'amber'
          ? 'bg-amber-100'
          : 'bg-red-100';

  return (
    <div className={`rounded-xl border p-4 overflow-hidden ${bgColors[status.level]}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${chipBg}`}>
          <svg className={`w-5 h-5 ${textColors[status.level]}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPaths[status.level]} />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className={`font-bold text-sm ${textColors[status.level]}`}>
            Passport: {status.label}
          </p>
          <p className={`text-xs mt-0.5 ${textColors[status.level]} opacity-80`}>
            {status.description}
          </p>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className={`text-center px-2 py-1.5 rounded-lg min-w-0 ${chipBg}`}>
              <p className={`text-sm font-extrabold leading-none ${textColors[status.level]}`}>{qualifications.length}</p>
              <p className={`text-[11px] mt-0.5 ${textColors[status.level]} opacity-70`}>quals</p>
            </div>
            <div className={`text-center px-2 py-1.5 rounded-lg min-w-0 ${chipBg}`}>
              <p className={`text-sm font-extrabold leading-none ${textColors[status.level]}`}>{workExperience?.length ?? 0}</p>
              <p className={`text-[11px] mt-0.5 ${textColors[status.level]} opacity-70`}>jobs</p>
            </div>
            <div className={`text-center px-2 py-1.5 rounded-lg min-w-0 ${chipBg}`}>
              <p className={`text-sm font-extrabold leading-none ${textColors[status.level]}`}>{documents.length}</p>
              <p className={`text-[11px] mt-0.5 ${textColors[status.level]} opacity-70`}>docs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
